import {addDays, format} from 'date-fns'

import type {
    BookingDays,
    DispatchAssignment,
    DispatchTimelineData,
    JobAllocation,
    UnassignedJob,
} from '../dispatch-timeline/types'
import {formatJobScheduleRange, parseApiDate, toApiDateString} from '../jobs/job-schedule-format'

type Yard = {id: string; name: string}

type BoardJob = UnassignedJob | DispatchAssignment

/** Another yard's job that the viewed yard's drivers are booked on. */
export type LentJob = {
    jobId: string
    yardName: string
    when: string
    jobName: string
    displayId: string
    repName: string
    customer: string
    where: string
    drivers: string[]
}

/** One driver booked on jobs from more than one yard on the same day. */
export type DoubleBooking = {
    driverId: string
    driverName: string
    day: string
    jobs: Array<{jobId: string; displayId: string; yardName: string}>
}

function boardJobs(data: DispatchTimelineData): Array<{jobId: string; job: BoardJob}> {
    const seen = new Set<string>()
    const jobs: Array<{jobId: string; job: BoardJob}> = []
    for (const job of [...data.unassignedJobs, ...data.assignments]) {
        const jobId = ('sourceJobId' in job ? job.sourceJobId : undefined) ?? job.id
        if (!seen.has(jobId)) {
            seen.add(jobId)
            jobs.push({jobId, job})
        }
    }
    return jobs
}

/** Real (non-placeholder) worker allocations; placeholders are shared third-party haulers. */
function workerAllocations(job: BoardJob): JobAllocation[] {
    return (job.allocations ?? []).filter(
        (allocation) => allocation.kind === 'worker' && !allocation.isPlaceholder,
    )
}

/** Days the allocation covers, inside the job's schedule and the loaded window. */
function bookedDays(
    job: BoardJob,
    days: BookingDays | undefined,
    window: {from?: string; until?: string},
): BookingDays | null {
    const schedule = job.schedule
    if (!schedule || schedule.dateTbc) {
        return null
    }
    const starts = [schedule.startDate, days?.from, window.from].filter(
        (value): value is string => value != null,
    )
    const ends = [schedule.endDate, days?.until, window.until].filter(
        (value): value is string => value != null,
    )
    const from = starts.reduce((latest, value) => (value > latest ? value : latest))
    const until = ends.length
        ? ends.reduce((first, value) => (value < first ? value : first))
        : null
    return until != null && until < from ? null : {from, until}
}

function yardNameFor(yards: Yard[], yardId: string | null | undefined): string {
    return yards.find((yard) => yard.id === yardId)?.name ?? 'Another yard'
}

/**
 * Lists other yards' jobs in the loaded window that the yard's own drivers are booked on,
 * so the lending yard can see where its drivers are.
 */
export function lentJobsForYard(
    data: DispatchTimelineData,
    yardId: string,
    yards: Yard[],
): LentJob[] {
    const window = {from: data.scheduleWindowStart, until: data.scheduleWindowEnd}
    const homeYards = new Map(data.resources.map((resource) => [resource.id, resource.homeYardId]))
    const lent: Array<[sortKey: string, job: LentJob]> = []
    for (const {jobId, job} of boardJobs(data)) {
        if (job.dispatchYardId == null || job.dispatchYardId === yardId) {
            continue
        }
        const ours = workerAllocations(job).filter(
            (allocation) =>
                homeYards.get(allocation.resourceId) === yardId &&
                bookedDays(job, allocation.days, window) != null,
        )
        if (ours.length === 0) {
            continue
        }
        const identity = data.jobIdentities?.[jobId]
        const schedule = job.schedule!
        lent.push([
            `${schedule.startDate} ${schedule.startTime ?? '99'} ${job.customer}`,
            {
                jobId,
                yardName: identity?.dispatchYardName ?? yardNameFor(yards, job.dispatchYardId),
                when: formatJobScheduleRange({
                    startDate: schedule.startDate,
                    endDate: schedule.endDate === schedule.startDate ? null : schedule.endDate,
                    startTime: schedule.startTime,
                    style: 'compact',
                }),
                jobName: job.label,
                displayId: identity?.displayId ?? job.jobCode,
                repName: identity?.repName ?? job.internalContactName ?? '',
                customer: identity?.customerName ?? job.customer,
                where: identity?.siteName ?? '',
                drivers: [...new Set(ours.map((allocation) => allocation.resourceName))],
            },
        ])
    }
    return lent.sort(([left], [right]) => left.localeCompare(right)).map(([, job]) => job)
}

/**
 * Finds drivers booked on two yards' jobs on the same day in the loaded window.
 * With a yard, keeps the bookings that touch it: its jobs or its drivers.
 */
export function crossYardDoubleBookings(
    data: DispatchTimelineData,
    yardId: string | null,
    yards: Yard[],
): DoubleBooking[] {
    const window = {from: data.scheduleWindowStart, until: data.scheduleWindowEnd}
    const lastDay = data.scheduleWindowEnd ?? data.scheduleWindowStart
    const byDriverDay = new Map<string, DoubleBooking>()
    const yardsByDriverDay = new Map<string, Set<string>>()
    for (const {jobId, job} of boardJobs(data)) {
        if (job.dispatchYardId == null) {
            continue
        }
        const identity = data.jobIdentities?.[jobId]
        for (const allocation of workerAllocations(job)) {
            const days = bookedDays(job, allocation.days, window)
            if (!days) {
                continue
            }
            const until = days.until ?? lastDay ?? days.from
            for (
                let day = days.from;
                day <= until;
                day = toApiDateString(addDays(parseApiDate(day), 1))
            ) {
                const key = `${allocation.resourceId} ${day}`
                const entry = byDriverDay.get(key) ?? {
                    driverId: allocation.resourceId,
                    driverName: allocation.resourceName,
                    day,
                    jobs: [],
                }
                const yardIds = yardsByDriverDay.get(key) ?? new Set<string>()
                if (!entry.jobs.some((existing) => existing.jobId === jobId)) {
                    entry.jobs.push({
                        jobId,
                        displayId: identity?.displayId ?? job.jobCode,
                        yardName:
                            identity?.dispatchYardName ?? yardNameFor(yards, job.dispatchYardId),
                    })
                    yardIds.add(job.dispatchYardId)
                }
                byDriverDay.set(key, entry)
                yardsByDriverDay.set(key, yardIds)
            }
        }
    }

    const homeYards = new Map(data.resources.map((resource) => [resource.id, resource.homeYardId]))
    return [...byDriverDay.entries()]
        .filter(([key, entry]) => {
            const yardIds = yardsByDriverDay.get(key)!
            return (
                yardIds.size > 1 &&
                (yardId == null || yardIds.has(yardId) || homeYards.get(entry.driverId) === yardId)
            )
        })
        .map(([, entry]) => entry)
        .sort((left, right) =>
            `${left.day} ${left.driverName}`.localeCompare(`${right.day} ${right.driverName}`),
        )
}

export function doubleBookingMessage(booking: DoubleBooking): string {
    const jobs = booking.jobs.map((job) => `${job.displayId} (${job.yardName})`)
    const day = format(parseApiDate(booking.day), 'EEE MMM d')
    return `${booking.driverName} is on ${jobs.slice(0, -1).join(', ')} and ${jobs.at(-1)} on ${day}`
}
